import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import apiAxios from 'helpers/apiAxios';

const baseUrl = '/authorization/user_sessions';

export const getSessionDashboard = createAsyncThunk(
  'sessionDashboard/get',
  async ({ startDate, endDate, granularity } = {}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${baseUrl}/dashboard`, {
        // granularity is a request, not an instruction: the server drops back
        // to a readable bucket size when the chosen one would not fit the range.
        params: { start_date: startDate, end_date: endDate, granularity },
      });
      return response.data?.data || {};
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getSessions = createAsyncThunk(
  'sessionDashboard/list',
  async ({
    scope = 'active', page = 1, perPage = 25,
    query = '', provider = '', network = '', startDate, endDate,
    sortBy = 'last_seen_at', sortDir = 'desc',
  } = {}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(baseUrl, {
        params: {
          scope, page, per_page: perPage,
          sort_by: sortBy,
          sort_dir: sortDir,
          q: query || undefined,
          provider: provider || undefined,
          network: network || undefined,
          // The API ignores dates on the active scope; sending them anyway would
          // only invite someone to "fix" the filter that appears to do nothing.
          start_date: scope === 'active' ? undefined : startDate,
          end_date: scope === 'active' ? undefined : endDate,
        },
      });
      return { scope, page, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getSessionFilterOptions = createAsyncThunk(
  'sessionDashboard/filterOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${baseUrl}/filter_options`);
      return response.data?.data || {};
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getGeoStatus = createAsyncThunk(
  'sessionDashboard/geoStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${baseUrl}/geo_status`);
      return response.data?.data || {};
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const updateGeoDatabase = createAsyncThunk(
  'sessionDashboard/updateGeo',
  async (provider, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${baseUrl}/update_geo_database`, { provider });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getThrottles = createAsyncThunk(
  'sessionDashboard/throttles',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${baseUrl}/throttles`, {
        params: {
          sort: params.sort || 'recent',
          network: params.network || undefined,
          rule: params.rule || undefined,
          q: params.q || undefined,
          page: params.page || 1,
          per_page: params.perPage || 10,
        },
      });
      return response.data?.data || {};
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const clearThrottle = createAsyncThunk(
  'sessionDashboard/clearThrottle',
  async (key, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${baseUrl}/clear_throttle`, { key });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const revokeSession = createAsyncThunk(
  'sessionDashboard/revoke',
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(`${baseUrl}/${sessionId}`);
      return { sessionId, message: response.data?.message };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const revokeAllSessions = createAsyncThunk(
  'sessionDashboard/revokeAll',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${baseUrl}/revoke_all`, { user_id: userId });
      return { userId, message: response.data?.message };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

const initialState = {
  stats: {},
  trend: { granularity: 'day', series: [] },
  range: {},
  browsers: [],
  providerOptions: [],
  geo: { available: null, running: false },
  throttles: {
    enabled: true, blocked: [], can_manage: false,
    total: 0, page: 1, per_page: 10, sort: 'recent', rules: [],
  },
  rows: [],
  totalRows: 0,
  scope: 'active',
  page: 1,
  sortBy: 'last_seen_at',
  sortDir: 'desc',
  loadingDashboard: false,
  loadingRows: false,
  revoking: false,
};

const sessionDashboardSlice = createSlice({
  name: 'authorization_session_dashboard',
  initialState,
  reducers: {
    setScope: (state, action) => {
      state.scope = action.payload;
      state.page = 1;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    // Clicking the active column flips direction; a different column starts
    // descending, which is what people expect from "show me the biggest first".
    setSort: (state, action) => {
      const column = action.payload;
      if (state.sortBy === column) {
        state.sortDir = state.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        state.sortBy = column;
        state.sortDir = 'desc';
      }
      state.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSessionDashboard.pending, (state) => {
        state.loadingDashboard = true;
      })
      .addCase(getSessionDashboard.rejected, (state) => {
        state.loadingDashboard = false;
      })
      .addCase(getSessionDashboard.fulfilled, (state, action) => {
        state.loadingDashboard = false;
        state.stats = action.payload.stats || {};
        state.trend = action.payload.trend || { granularity: 'day', series: [] };
        state.range = action.payload.range || {};
        state.browsers = action.payload.browsers || [];
      })

      .addCase(getSessionFilterOptions.fulfilled, (state, action) => {
        state.providerOptions = action.payload.providers || [];
      })

      .addCase(getThrottles.fulfilled, (state, action) => {
        state.throttles = action.payload;
      })
      .addCase(clearThrottle.fulfilled, (state, action) => {
        toast.success(action.payload?.message || 'Rate limit cleared.');
      })
      .addCase(clearThrottle.rejected, (state, action) => {
        toast.error(action.payload?.message || 'Could not clear the rate limit.');
      })

      .addCase(getGeoStatus.fulfilled, (state, action) => {
        state.geo = action.payload;
      })
      .addCase(updateGeoDatabase.pending, (state) => {
        // Flipped immediately so the button disables on click; the poll that
        // follows replaces this with the server's own view.
        state.geo = { ...state.geo, running: true };
      })
      .addCase(updateGeoDatabase.fulfilled, (state, action) => {
        toast.success(action.payload?.message || 'Download started.');
      })
      .addCase(updateGeoDatabase.rejected, (state, action) => {
        state.geo = { ...state.geo, running: false };
        toast.error(action.payload?.message || 'Could not start the download.');
      })

      .addCase(getSessions.pending, (state) => {
        state.loadingRows = true;
      })
      .addCase(getSessions.rejected, (state) => {
        state.loadingRows = false;
      })
      .addCase(getSessions.fulfilled, (state, action) => {
        state.loadingRows = false;
        state.rows = action.payload.rows || [];
        state.totalRows = action.payload.total_rows || 0;
      })

      .addCase(revokeSession.pending, (state) => {
        state.revoking = true;
      })
      .addCase(revokeSession.rejected, (state) => {
        state.revoking = false;
      })
      .addCase(revokeSession.fulfilled, (state, action) => {
        state.revoking = false;
        toast.success(action.payload.message || 'Session signed out.');
      })

      .addCase(revokeAllSessions.pending, (state) => {
        state.revoking = true;
      })
      .addCase(revokeAllSessions.rejected, (state) => {
        state.revoking = false;
      })
      .addCase(revokeAllSessions.fulfilled, (state, action) => {
        state.revoking = false;
        toast.success(action.payload.message || 'Sessions signed out.');
      });
  },
});

export const { setScope, setPage, setSort } = sessionDashboardSlice.actions;
export default sessionDashboardSlice.reducer;
